<template>
  <div class="content-body">
    <b-form>
      <b-row class="uploads">
        <b-col md="4">
          <div class="font-weight-bold mb-3 text-center">
            Government ID of Signatory
            <span class="text-danger ml-1">*</span>
          </div>
          <b-container
            @click="uploadGovernmentId"
            class="mt-2 round-dotted-border text-center upload-container"
            @drop="dropGovernmentId"
            @dragover="(e) => e.preventDefault()"
          >
            <span v-if="form.government_id">
              {{ form.government_id.name | shortenFilename }}
            </span>
            <span v-else>
              Drag file here or <u class="font-weight-bold">browse</u>
            </span>
            <b-form-file
              accept=".jpg, .jpeg, .pdf"
              id="upload-government-id"
              ref="government_id"
              style="display: none"
              v-model="form.government_id"
            />
          </b-container>
          <div
            v-if="uploadedGovernmentId && statuses[uploadedGovernmentId.status]"
            class="mt-3 text-center"
          >
            <span class="font-weight-bold">Progress: </span>
            <span :class="`${uploadedGovernmentId.status}-status`">
              {{ statuses[uploadedGovernmentId.status] }}
            </span>
            <a
              :href="uploadedGovernmentId.link"
              target="_blank"
              rel="noopener"
              class="ml-2 cursor-pointer"
            >
              <img
                src="@/assets/images/actions/view.svg"
                alt="View Button"
                v-b-popover.hover.bottom="'View uploaded file'"
              />
            </a>
          </div>
        </b-col>

        <b-col md="8">
          <div class="font-weight-bold mb-3 text-center">
            Other Government Documents &#40;any of the following: Mayor's Permit, SEC,
            DTI&#41;
            <span class="text-danger ml-1">*</span>
          </div>
          <b-container
            @click="uploadOtherGovernmentDocu"
            class="mt-2 round-dotted-border text-center upload-container"
            @drop="dropOtherGovernmentDocu"
            @dragover="(e) => e.preventDefault()"
          >
            <span v-if="form.other_government_docu">
              {{ form.other_government_docu.name | shortenFilename }}
            </span>
            <span v-else
              >Drag file here or <u class="font-weight-bold">browse</u></span
            >

            <b-form-file
              accept=".jpg, .jpeg, .pdf"
              id="upload-other-government-docu"
              ref="other_government_docu"
              style="display: none"
              v-model="form.other_government_docu"
            />
          </b-container>
          <div
            v-if="
              uploadedOtherGovernmentDocuments &&
              statuses[uploadedOtherGovernmentDocuments.status]
            "
            class="mt-3 text-center"
          >
            <span class="font-weight-bold">Progress: </span>
            <span :class="`${uploadedOtherGovernmentDocuments.status}-status`">
              {{ statuses[uploadedOtherGovernmentDocuments.status] }}
            </span>
            <a
              :href="uploadedOtherGovernmentDocuments.link"
              target="_blank"
              rel="noopener"
              class="ml-2 cursor-pointer"
            >
              <img
                src="@/assets/images/actions/view.svg"
                alt="View Button"
                v-b-popover.hover.bottom="'View uploaded file'"
              />
            </a>
          </div>
        </b-col>
      </b-row>

      <div class="content-inside">
        <div class="font-weight-bold mb-3">Bank Details:</div>
        <div class="px-4">
          <b-form-group v-for="(field, i) in fields" :key="i">
            <b-container>
              <b-row>
                <b-col md="3">
                  <template #label>
                    <span class="font-weight-bold">{{ field.label }}</span>
                    <i
                      class="ic-tooltip ml-1"
                      v-if="field.tooltip"
                      v-b-popover.hover.bottom="field.tooltip"
                    ></i>
                    <span v-if="field.required" class="text-danger ml-1"
                      >*</span
                    >
                  </template>
                </b-col>
                <b-col>
                  <b-form-textarea
                    v-if="field.type === 'textarea'"
                    v-model="form[field.model]"
                    rows="3"
                    max-rows="6"
                    :disabled="field.disabled"
                    :maxlength="field.maxLength"
                  />
                  <b-input-group
                    v-if="field.type === 'number'"
                    :prepend="field.model === 'mobile_number' ? '+63' : ''"
                  >
                    <numeric-input
                      v-model="form[field.model]"
                      :disabled="field.disabled"
                      :max-length="field.maxLength"
                    />
                  </b-input-group>
                  <b-form-select
                    v-if="field.type === 'select'"
                    v-model="form[field.model]"
                    :options="field.options"
                    :class="field.class"
                    :disabled="field.disabled"
                  >
                  </b-form-select>
                  <b-form-input
                    v-if="field.type === 'text'"
                    v-model="form[field.model]"
                    :disabled="field.disabled"
                    :maxlength="field.maxLength"
                  />
                  <alpha-numeric-input
                    v-if="field.type === 'alphanumeric'"
                    v-model="form[field.model]"
                    :disabled="field.disabled"
                    :max-length="field.maxLength"
                  />
                  <b-form-input
                    v-if="field.type === 'email'"
                    type="email"
                    v-model="form[field.model]"
                    :disabled="field.disabled"
                    :maxlength="field.maxLength"
                  />
                  <b-form-invalid-feedback :state="isValidField(field.model)">
                    {{ getErrorMessage(field.model) }}
                  </b-form-invalid-feedback>
                </b-col>
              </b-row>
            </b-container>
          </b-form-group>
        </div>
        <b-row class="text-center mt-5">
          <b-col>
            <b-button
              variant="primary"
              class="px-5 save-button"
              @click="submit"
              :disabled="
                isInvalidFields || isInvalidFiles || loading || !hasChanges
              "
            >
              Submit
            </b-button>
          </b-col>
        </b-row>
      </div>
    </b-form>
  </div>
</template>

<script>
import NumericInput from "@/components/common/NumericInput";
import AlphaNumericInput from "@/components/common/AlphaNumericInput";
import { validateErrorsState } from "@/mixins/validate";
import _ from "lodash";
import { mapState, mapActions } from "vuex";
import notification from "@/services/notification";

const initialForm = () => ({
  name: null,
  address: null,
  mobile_number: null,
  email_address: null,
  tin_number: null,
  bank_account_currency: "PHP",
  bank_name: null,
  bank_address: null,
  bank_account_name: null,
  bank_account_number: null,
  bank_swift_code: null,
  government_id: null,
  other_government_docu: null,
});

export default {
  mixins: [validateErrorsState],
  components: {
    NumericInput,
    AlphaNumericInput,
  },
  data() {
    return {
      fields: [
        {
          label: "Vendor Name",
          type: "text",
          model: "name",
          maxLength: 100,
          required: true,
        },
        {
          label: "Business Address",
          type: "textarea",
          model: "address",
          maxLength: 200,
          required: true,
        },
        {
          label: "Contact Number",
          type: "number",
          model: "mobile_number",
          maxLength: 10,
          required: true,
        },
        {
          label: "Email Address",
          type: "email",
          model: "email_address",
          maxLength: 80,
          required: true,
        },
        {
          label: "TIN",
          type: "number",
          model: "tin_number",
          maxLength: 12,
          required: true,
        },
        {
          label: "Currency",
          type: "select",
          model: "bank_account_currency",
          options: [{ value: "PHP", text: "PHP" }],
          disabled: true,
          required: true,
          class: "w-25"
        },
        {
          label: "Bank Name",
          type: "select",
          model: "bank_name",
          options: [
            {value: "Banco de Oro", text: "Banco de Oro"},
            {value: "Amanah Islamic Investment Bank", text: "Amanah Islamic Investment Bank"},
            {value: "Australia and New Zealand Bank", text: "Australia and New Zealand Bank"},
            {value: "Asia United Bank (AUB)", text: "Asia United Bank (AUB)"},
            {value: "Bank of China, Ltd. - Manila Branch", text: "Bank of China, Ltd. - Manila Branch"},
            {value: "Banco De Oro Unibank, Inc.", text: "Banco De Oro Unibank, Inc."},
            {value: "Bangkok Bank Public Co, Ltd.", text: "Bangkok Bank Public Co, Ltd."},
            {value: "Bangko Kabayan Inc", text: "Bangko Kabayan Inc"},
            {value: "Bangko Mabuhay (A Rural Bank), Inc.", text: "Bangko Mabuhay (A Rural Bank), Inc."},
            {value: "Bangko Nuestra Señora Del Pilar Inc", text: "Bangko Nuestra Señora Del Pilar Inc"},
            {value: "Bank of America, N.A.", text: "Bank of America, N.A."},
            {value: "Bank of Commerce", text: "Bank of Commerce"},
            {value: "Bank of Florida", text: "Bank of Florida"},
            {value: "Bank of Makati", text: "Bank of Makati"},
            {value: "Bank of the Philippine Islands (BPI)", text: "Bank of the Philippine Islands (BPI)"},
            {value: "BPI Direct BanKO, Inc., A Savings Bank", text: "BPI Direct BanKO, Inc., A Savings Bank"},
            {value: "Binangonan Rural Bank (BRBDigital)", text: "Binangonan Rural Bank (BRBDigital)"},
            {value: "Cantilan Bank", text: "Cantilan Bank"},
            {value: "Country Builders Bank Inc", text: "Country Builders Bank Inc"},
            {value: "China Banking Corporation", text: "China Banking Corporation"},
            {value: "CIS Bayad Center, Inc. (CBCI)", text: "CIS Bayad Center, Inc. (CBCI)"},
            {value: "China Bank Savings", text: "China Bank Savings"},
            {value: "Cebuana Lhuillier Rural Bank, Inc.", text: "Cebuana Lhuillier Rural Bank, Inc."},
            {value: "CIMB Bank Philippines", text: "CIMB Bank Philippines"},
            {value: "Citibank, N.A.", text: "Citibank, N.A."},
            {value: "Camalig Bank", text: "Camalig Bank"},
            {value: "Coins", text: "Coins"},
            {value: "Netbank (Community Rural Bank of Romblon)", text: "Netbank (Community Rural Bank of Romblon)"},
            {value: "CARD Bank", text: "CARD Bank"},
            {value: "CARD SME Bank, Inc., A Thrift Bank", text: "CARD SME Bank, Inc., A Thrift Bank"},
            {value: "CTBC Bank (Philippines) Corp.", text: "CTBC Bank (Philippines) Corp."},
            {value: "Dungganon Bank (A Microfinance Rural Bank), Inc.", text: "Dungganon Bank (A Microfinance Rural Bank), Inc."},
            {value: "Development Bank of the Philippines", text: "Development Bank of the Philippines"},
            {value: "Dumaguete City Development Bank", text: "Dumaguete City Development Bank"},
            {value: "DCPAY Philippines", text: "DCPAY Philippines"},
            {value: "Deutsche Bank AG", text: "Deutsche Bank AG"},
            {value: "Equicom Savings Bank, Inc.", text: "Equicom Savings Bank, Inc."},
            {value: "East West Banking Corporation", text: "East West Banking Corporation"},
            {value: "EastWest Rural Bank or Komo", text: "EastWest Rural Bank or Komo"},
            {value: "First Consolidated Bank Inc", text: "First Consolidated Bank Inc"},
            {value: "GCash", text: "GCash"},
            {value: "GoTyme Bank", text: "GoTyme Bank"},
            {value: "Grabpay", text: "Grabpay"},
            {value: "Guagua Rural Bank Inc", text: "Guagua Rural Bank Inc"},
            {value: "Hong Kong and Shanghai Banking Corp.", text: "Hong Kong and Shanghai Banking Corp."},
            {value: "HSBC Savings Bank Inc", text: "HSBC Savings Bank Inc"},
            {value: "Innovative Rural Bank Inc", text: "Innovative Rural Bank Inc"},
            {value: "Industrial Bank of Korea", text: "Industrial Bank of Korea"},
            {value: "Industrial and Commercial Bank of China (ICBC) - Manila Branch", text: "Industrial and Commercial Bank of China (ICBC) - Manila Branch"},
            {value: "ISLA Bank", text: "ISLA Bank"},
            {value: "JP Morgan Chase Bank, NA.", text: "JP Morgan Chase Bank, NA."},
            {value: "Zybi Tech Inc.", text: "Zybi Tech Inc."},
            {value: "Korea Exchange Bank", text: "Korea Exchange Bank"},
            {value: "KEB Hana Bank - Manila Branch", text: "KEB Hana Bank - Manila Branch"},
            {value: "Land Bank of The Philippines", text: "Land Bank of The Philippines"},
            {value: "Legazpi Savings Bank, Inc.", text: "Legazpi Savings Bank, Inc."},
            {value: "Lulu Financial Services (Phils) Inc.", text: "Lulu Financial Services (Phils) Inc."},
            {value: "Maya Bank, Inc.", text: "Maya Bank, Inc."},
            {value: "Mindanao Consolidated Cooperative Bank", text: "Mindanao Consolidated Cooperative Bank"},
            {value: "Mega International Commercial Bank Co, Ltd.", text: "Mega International Commercial Bank Co, Ltd."},
            {value: "Metropolitan Bank and Trust Company (Metrobank)", text: "Mega International Commercial Bank Co, Ltd."},
            {value: "Mizuho Bank, Ltd. - Manila Branch", text: "Mizuho Bank, Ltd. - Manila Branch"},
            {value: "Maybank Philippines", text: "Maybank Philippines"},
            {value: "Malayan Bank Savings and Mortgage Bank, Inc.", text: "Malayan Bank Savings and Mortgage Bank, Inc."},
            {value: "MUFG Bank, Ltd.", text: "MUFG Bank, Ltd."},
            {value: "Omnipay, Inc.", text: "Omnipay, Inc."},
            {value: "BDO Network Bank", text: "BDO Network Bank"},
            {value: "Partner Rural Bank (Cotabato), Inc.", text: "Partner Rural Bank (Cotabato), Inc."},
            {value: "Paymaya", text: "Paymaya"},
            {value: "Philippine Business Bank, Inc., A Savings Bank", text: "Philippine Business Bank, Inc., A Savings Bank"},
            {value: "Philippine Bank of Communications (PBCOM)", text: "Philippine Bank of Communications (PBCOM)"},
            {value: "Philippine National Bank (PNB)", text: "Philippine National Bank (PNB)"},
            {value: "PNB Savings Bank", text: "PNB Savings Bank"},
            {value: "PalawanPay", text: "PalawanPay"},
            {value: "Producers Bank", text: "Producers Bank"},
            {value: "Philippine Savings Bank (PSBank)", text: "Philippine Savings Bank (PSBank)"},
            {value: "Philippine Trust Company", text: "Philippine Trust Company"},
            {value: "Philippine Veterans Bank", text: "Philippine Veterans Bank"},
            {value: "Queen City Development Bank, Inc.", text: "Queen City Development Bank, Inc."},
            {value: "Quezon Capital Rural Bank, Inc", text: "Quezon Capital Rural Bank, Inc"},
            {value: "Rang-Ay Bank Inc", text: "Rang-Ay Bank Inc"},
            {value: "Rural Bank of Digos", text: "Rural Bank of Digos"},
            {value: "Rural Bank of Guinobatan, Inc.", text: "Rural Bank of Guinobatan, Inc."},
            {value: "Rural Bank of Montalban, Inc", text: "Rural Bank of Montalban, Inc"},
            {value: "Rizal Commercial Banking Corporation (RCBC)", text: "Rizal Commercial Banking Corporation (RCBC)"},
            {value: "Robinsons Bank", text: "Robinsons Bank"},
            {value: "Sterling Bank of Asia", text: "Sterling Bank of Asia"},
            {value: "Standard Chartered Bank", text: "Standard Chartered Bank"},
            {value: "Seabank Philippines Inc.", text: "Seabank Philippines Inc."},
            {value: "Security Bank Corporation", text: "Security Bank Corporation"},
            {value: "Shinhan Bank - Manila Branch", text: "Shinhan Bank - Manila Branch"},
            {value: "ShopeePay", text: "ShopeePay"},
            {value: "Sumitomo Mitsui Banking Corporation (SMBC)", text: "Sumitomo Mitsui Banking Corporation (SMBC)"},
            {value: "Starpay Corporation", text: "Starpay Corporation"},
            {value: "Sun Savings Bank", text: "Sun Savings Bank"},
            {value: "Tayocash Inc", text: "Tayocash Inc"},
            {value: "Tonik Digital Bank, Inc.", text: "Tonik Digital Bank, Inc."},
            {value: "Tong Yang Savings Bank, Inc.", text: "Tong Yang Savings Bank, Inc."},
            {value: "Union Bank of the Philippines (UBP)", text: "Union Bank of the Philippines (UBP)"},
            {value: "UCPB Savings bank", text: "UCPB Savings bank"},
            {value: "United Coconut Planters Bank (UCPB)", text: "United Coconut Planters Bank (UCPB)"},
            {value: "United Overseas Bank, Ltd. (UOB) - Manila Branch", text: "United Overseas Bank, Ltd. (UOB) - Manila Branch"},
            {value: "USSC Money Services Inc.", text: "USSC Money Services Inc."},
            {value: "Wealth Development Bank Corporation", text: "Wealth Development Bank Corporation"}
          ],
          maxLength: 100,
          required: true,
          class: "w-100"
        },
        {
          label: "Bank Address",
          type: "textarea",
          model: "bank_address",
          maxLength: 200,
          required: true,
        },
        {
          label: "Account Name",
          type: "text",
          model: "bank_account_name",
          maxLength: 80,
          required: true,
        },
        {
          label: "Account Number",
          type: "number",
          model: "bank_account_number",
          maxLength: 30,
          required: true,
        },
        {
          label: "Bank Swift Code",
          type: "alphanumeric",
          model: "bank_swift_code",
          maxLength: 11,
          required: true,
          tooltip:
            "Used to identify banks and financial institutions globally. You can typically find them on a bank statement or on your bank's website.",
        },
      ],
      form: initialForm(),
      statuses: {
        pending: "On-Going Review",
        approved: "Approved",
        declined: "Denied",
      },
      uploadedGovernmentId: null,
      uploadedOtherGovernmentDocuments: null,
      hasChanges: false,
      isDataLoaded: false,
    };
  },
  computed: {
    ...mapState("accountDocument", ["loading", "errors", "bankDetails"]),
    isInvalidFields() {
      return (
        !this.fields.every((field) => !!this.form[field.model]) ||
        !_.isEmpty(this.errors)
      );
    },
    isInvalidFiles() {
      if (!this.uploadedGovernmentId && !this.form.government_id) return true;
      if (
        !this.uploadedOtherGovernmentDocuments &&
        !this.form.other_government_docu
      )
        return true;

      return false;
    },
  },
  methods: {
    ...mapActions("accountDocument", ["uploadDocument"]),

    uploadGovernmentId() {
      document.getElementById("upload-government-id").click();
    },
    uploadOtherGovernmentDocu() {
      document.getElementById("upload-other-government-docu").click();
    },
    async submit() {
      this.hasChanges = false;
      
      let msg = "";

      if (this.form.government_id) msg = "documents and";

      if (this.form.other_government_docu) msg = "documents and";

      const { ...bankDetailsPayload } = this.form;

      return await Promise.all([
        this.uploadGovernmentDocument("government_id"),
        this.uploadGovernmentDocument("other_government_docu"),
        this.$store.dispatch(
          "accountDocument/saveBankDetails",
          bankDetailsPayload
        ),
      ])
        .then(() => {
          notification.success(
            `Successfully saved merchant's ${msg} bank details.`
          );
        })
        .catch(() => {
          notification.warning(`Oops, cannot save ${msg} bank details.`);
        })
        .finally(() => {
          this.form = initialForm();
          this.loadData();
        });
    },
    async uploadGovernmentDocument(type) {
      if (this.form[type]) {
        let payload = new FormData();

        payload.append("file", await this.toBase64(this.form[type]));
        payload.append("type", type);

        return await this.uploadDocument(payload);
      }
    },
    toBase64(file) {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = (e) => {
          return resolve(e.target.result.split(",")[1]);
        };
      });
    },
    dropGovernmentId(e) {
      e.preventDefault();
      this.$refs.government_id.files = e.dataTransfer.files[0];
      this.form.government_id = e.dataTransfer.files[0];
    },
    dropOtherGovernmentDocu(e) {
      e.preventDefault();
      this.$refs.other_government_docu.files = e.dataTransfer.files[0];
      this.form.other_government_docu = e.dataTransfer.files[0];
    },
    async loadData() {
      this.isDataLoaded = false;

      const governmentIdKey = "government_id";
      const otherDocumentsKey = "other_government_docu";

      await this.$store.dispatch("accountDocument/getBankDetails");

      this.fields.map(({ model }) => {
        if (this.bankDetails[model]) this.form[model] = this.bankDetails[model];
      });
      this.uploadedGovernmentId = this.bankDetails.documents.find(
        (document) => document.type === governmentIdKey && document.link
      );
      this.uploadedOtherGovernmentDocuments = this.bankDetails.documents.find(
        (document) => document.type === otherDocumentsKey && document.link
      );
      this.$nextTick(() => {
        this.isDataLoaded = true;
      });
    },
  },
  watch: {
    form: {
      handler() {
        if (this.isDataLoaded) this.hasChanges = true;
      },
      deep: true,
    },
    "form.mobile_number"(value) {
      const errors = this.errors ? this.errors : {};

      if ("mobile_number" in errors) delete errors.mobile_number;

      if (value && !/^9[0-9]{9}$/.test(value))
        errors.mobile_number = "Please enter a valid mobile number.";

      this.$store.commit("accountDocument/errors", errors);
    },
    "form.email_address"(value) {
      const errors = this.errors ? this.errors : {};

      if ("email_address" in errors) delete errors.email_address;

      if (value && !/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(value))
        errors.email_address = "Please enter a valid email addrress.";

      this.$store.commit("accountDocument/errors", errors);
    },
    "form.tin_number"(value) {
      if (value === "-") {
        this.form.tin_number = "";
        return;
      }

      const errors = this.errors ? this.errors : {};

      if ("tin_number" in errors) delete errors.tin_number;

      if (value && value.length < 9)
        errors.tin_number = "Please enter a valid TIN.";

      this.$store.commit("accountDocument/errors", errors);
    },
    "form.bank_account_number"(value) {
      const errors = this.errors ? this.errors : {};

      if ("bank_account_number" in errors) delete errors.bank_account_number;

      if (value && value.length < 10)
        errors.bank_account_number =
          "Please enter a valid bank account number.";

      this.$store.commit("accountDocument/errors", errors);
    },
    "form.bank_swift_code"(value) {
      const errors = this.errors ? this.errors : {};

      if ("bank_swift_code" in errors) delete errors.bank_swift_code;

      if (value) {
        this.form.bank_swift_code = value.toUpperCase();

        if (!/^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/.test(value))
          errors.bank_swift_code = "Please enter a valid bank swift code.";
      }

      this.$store.commit("accountDocument/errors", errors);
    },
  },
  async created() {
    await this.loadData();
  },
  filters: {
    shortenFilename(filename) {
      const length = 25;
      return filename.length > length
        ? `${filename.substring(0, length)}...${filename.split(".").pop()}`
        : filename;
    },
  },
};
</script>

<style scoped>
.upload-container {
  cursor: pointer;
  background: #f6f9fb;
  height: 100px;
  width: 300px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}
.progress-status {
  padding-top: 32px;
  padding-left: 32px;
  padding-right: 32px;
}
.uploads {
  padding-bottom: 32px;
  padding-left: 32px;
  padding-right: 32px;
}
.pending-status {
  color: #f1c232;
}
.approved-status {
  color: #009e0f;
}
.declined-status {
  color: #cf2a27;
}
</style>
